import { Injectable, inject } from '@angular/core';
import { Observable, tap } from 'rxjs';
import {
  CreateSyncConfigurationRequest,
  FavoriteList,
  OutlookSyncType,
  SyncHealthStatus,
  SyncHistory,
  UpdateSyncConfiguration,
  UserSyncConfiguration,
} from '../models';
import { OutlookSyncStore } from '../state/outlook-sync.store';
import { UserSyncConfigurationService } from './user-sync-configuration.service';

@Injectable({ providedIn: 'root' })
export class SyncConfigurationFacade {
  private readonly service = inject(UserSyncConfigurationService);
  private readonly store = inject(OutlookSyncStore);

  getConfiguration(
    userId: string,
    workspaceId: string,
  ): Observable<UserSyncConfiguration | undefined> {
    return this.service.getConfiguration(userId, workspaceId);
  }

  getConfigurationForInstallPrecheck(
    userId: string,
    workspaceId: string,
  ): Observable<UserSyncConfiguration | undefined> {
    return this.service.getConfigurationForInstallPrecheck(userId, workspaceId);
  }

  getConfigurationById(
    syncConfigurationId: string,
  ): Observable<UserSyncConfiguration | undefined> {
    return this.service.getConfigurationById(syncConfigurationId);
  }

  updateConfiguration(
    settings: UpdateSyncConfiguration,
    id: string,
  ): Observable<boolean> {
    // Save-state is caller-owned: non-save flows (auto-heal, install re-enable) never complete the beginSave cycle.
    return this.service.updateConfiguration(settings, id);
  }

  getHistoryBySyncType(
    type: OutlookSyncType,
    id: string,
    pageSize = 5,
    offset = 0,
  ): Observable<SyncHistory[] | undefined> {
    return this.service
      .getHistoryBySyncType(type, id, pageSize, offset)
      .pipe(tap((history) => history && this.store.setLastHistory(type, history)));
  }

  getHealthStatus(id: string): Observable<SyncHealthStatus | undefined> {
    return this.service.getHealthStatus(id);
  }

  getFavoriteLists(syncConfigurationId: string): Observable<FavoriteList[]> {
    return this.service.getFavoriteLists(syncConfigurationId);
  }

  createConfiguration(
    request: CreateSyncConfigurationRequest,
  ): Observable<string> {
    return this.service.createConfiguration(request);
  }
}
